import React, { useEffect, useState, useRef } from 'react';
import Header from './Header.jsx';
import Footer from './Footer.jsx';
import InquiryBanner from './InquiryBanner.jsx';
import banner from '../assets/banners/img2.avif';
import { FiCheckCircle, FiShield, FiSettings, FiTrendingUp, FiAward, FiZap, FiRotateCw } from 'react-icons/fi';
import p6 from '../assets/products/butterfly-valves.jpg';
import '../css/butterflyValves.css';

const keyFeatures = [
  {
    title: 'Compact Wafer Design',
    description: 'Wafer-type design requires minimal space between flanges, making butterfly valves ideal for space-constrained installations.'
  },
  {
    title: 'Low Pressure Drop',
    description: 'Streamlined disc design provides excellent flow characteristics with minimal pressure drop across the valve.'
  },
  {
    title: 'Quick Operation',
    description: 'Quarter-turn operation provides fast opening and closing, enabling efficient flow control in process applications.'
  },
  {
    title: 'Cost-Effective Solution',
    description: 'Lightweight construction and simple design result in lower material costs and reduced installation expenses.'
  }
];

const specifications = [
  {
    icon: <FiSettings />,
    label: 'Size Range',
    value: '2" – 48"',
    detail: 'Wide range from 2 inches to 48 inches'
  },
  {
    icon: <FiTrendingUp />,
    label: 'Pressure Rating',
    value: 'Class 150 – 300',
    detail: 'Suitable for low to medium pressure applications'
  },
  {
    icon: <FiShield />,
    label: 'Materials',
    value: 'Ductile Iron, Stainless Steel, Carbon Steel',
    detail: 'Multiple material options for various services'
  },
  {
    icon: <FiAward />,
    label: 'Standards',
    value: 'API 609, BS EN 593',
    detail: 'Compliant with international standards'
  }
];

const valveTypes = [
  {
    name: 'Wafer Type Butterfly',
    description: 'Standard wafer design installed between two flanges, ideal for space-saving applications in pipelines.',
    features: ['Compact installation', 'Lightweight design', 'Cost-effective', 'Easy maintenance']
  },
  {
    name: 'Lug Type Butterfly',
    description: 'Lug design with threaded inserts, allowing valve removal without affecting the pipeline system.',
    features: ['Threaded inserts', 'Easy removal', 'Secure installation', 'Versatile applications']
  },
  {
    name: 'Double Flanged Butterfly',
    description: 'Full flanged design with bolted connections, providing secure installation and easy access for maintenance.',
    features: ['Full flanged', 'Secure sealing', 'Standard connections', 'Easy access']
  },
  {
    name: 'High-Performance Butterfly',
    description: 'Heavy-duty design engineered for high-pressure applications requiring superior performance and reliability.',
    features: ['High-pressure capability', 'Enhanced durability', 'Reliable operation', 'Superior sealing']
  }
];

const advantages = [
  {
    icon: <FiZap />,
    title: 'Excellent Flow Control',
    description: 'Butterfly valves provide excellent flow control capabilities with quarter-turn operation, making them ideal for throttling and on/off applications.'
  },
  {
    icon: <FiShield />,
    title: 'Space-Saving Design',
    description: 'Compact wafer and lug designs require minimal space between flanges, making butterfly valves ideal for space-constrained installations.'
  },
  {
    icon: <FiSettings />,
    title: 'Economical Solution',
    description: 'Lightweight construction and simple design result in lower material costs, reduced installation time, and overall cost savings compared to other valve types.'
  },
  {
    icon: <FiRotateCw />,
    title: 'Quick Operation',
    description: 'Quarter-turn operation provides fast opening and closing, enabling efficient flow control and system response in process applications.'
  }
];

const applications = [
  {
    title: 'Water Distribution',
    description: 'Municipal and industrial water distribution systems requiring reliable flow control and shut-off capabilities.'
  },
  {
    title: 'HVAC Systems',
    description: 'Heating, ventilation, and air conditioning systems where compact design and excellent flow characteristics are essential.'
  },
  {
    title: 'Chemical Processing',
    description: 'Chemical processing plants requiring versatile butterfly valves for various chemical services and process lines.'
  },
  {
    title: 'Power Generation',
    description: 'Power generation facilities where reliable butterfly valves are needed for cooling water, steam, and auxiliary systems.'
  },
  {
    title: 'Food & Beverage',
    description: 'Food and beverage processing industries requiring sanitary butterfly valves with excellent cleanability and flow control.'
  },
  {
    title: 'Wastewater Treatment',
    description: 'Wastewater treatment facilities where compact design, reliable operation, and cost-effectiveness are essential.'
  }
];

const ButterflyValves = () => {
  const sectionsRef = useRef([]);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('bfv-animate');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const sections = sectionsRef.current;
    sections.forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      sections.forEach((section) => {
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <Header />
      
      {/* Hero Banner Section */}
      <section className="bfv-hero">
        <div className="bfv-hero-background">
          <img
            src={banner}
            alt="Butterfly Valves Banner"
            className="bfv-hero-image"
          />
          <div className="bfv-hero-overlay"></div>
        </div>
        <div className="bfv-hero-content">
          <h1 className="bfv-hero-title">Butterfly Valves</h1>
          <p className="bfv-hero-subtitle">
            Compact and efficient butterfly valves designed for excellent flow control and space-saving installations
          </p>
        </div>
      </section>

      {/* Introduction Section */}
      <div 
        ref={(el) => (sectionsRef.current[0] = el)}
        className="bfv-section bfv-intro"
      >
        <div className="bfv-container">
          <div className="bfv-intro-grid">
            <div className="bfv-intro-text">
              <h2 className="bfv-intro-title"> Butterfly Valve</h2>
              <p className="bfv-intro-description">
                Design and manufacturing according to API 609, triple offset design. Friction-free on sealing surface, 
                metal to metal sealing, reliable performance. Materials available include carbon steel, low temperature 
                carbon steel, low alloy steel, austenitic stainless steels, austenitic-ferritic duplex stainless steel, 
                nickel alloys with soft seat as sealing component. Due higher torque in floating ball piping components, 
                for higher sizes trunnion mounted ball piping components are recommended.
              </p>
            </div>
            <div className="bfv-intro-image">
              <img src="/butterfly.PNG" alt="Butterfly Valves" className="bfv-intro-img" />
            </div>
          </div>
        </div>
      </div>

      {/* Product Range, Technical Specifications, and Design Features Section */}
      <div 
        ref={(el) => (sectionsRef.current[1] = el)}
        className="bfv-section bfv-specs"
      >
        <div className="bfv-container">
          <div className="bfv-hero-specs-row">
            <div className="bfv-spec-card-horizontal">
              <h3>Product Range</h3>
              <ul>
                <li><strong>Piping component size:</strong> 2" ~ 24" (DN50-DN600)</li>
                <li><strong>Pressure:</strong> ANSI Class 150~300</li>
                <li><strong>Temperature:</strong> -196℃~ 500℃</li>
                <li><strong>Body Materials:</strong> Carbon Steel, Stainless Steel, Alloy Steel, Duplex Steel</li>
                <li><strong>Trim (Disc/Stem):</strong> Carbon Steel, Stainless Steel, Duplex Steel, Alloy Steel</li>
                <li><strong>Seat:</strong> TFM, Nylon and laminar metal seat</li>
              </ul>
            </div>
            <div className="bfv-spec-card-horizontal">
              <h3>Technical Specifications</h3>
              <ul>
                <li><strong>Design Standard:</strong> API 609 / ASME B 16.34</li>
                <li><strong>Structure length:</strong> API 609</li>
                <li><strong>Pipe flanges:</strong> ANSI B16.5</li>
                <li><strong>Butt end size:</strong> ANSI B16.25</li>
                <li><strong>Inspection and Testing:</strong> API 609/ISO 5208</li>
                <li><strong>Transmission:</strong> Manual, Gear Box, Actuator</li>
              </ul>
            </div>
            <div className="bfv-spec-card-horizontal">
              <h3>Design Features</h3>
              <ul>
                <li>Anti Blowout</li>
                <li>Triple Offset seat construction</li>
                <li>Extended Bonnet for Cryogenic Services</li>
                <li>Locking Device as an Optional</li>
                <li>Extended stem construction for Underground Service</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Key Features Section */}
      

      {/* Valve Types Section */}
      
      {/* Advantages Section */}
      

      {/* Applications Section */}
     

    
      <Footer />

      {scrollY > 300 && (
        <button 
          className="bfv-scroll-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          ↑
        </button>
      )}
    </>
  );
};

export default ButterflyValves;








